import React, { useContext, useState } from 'react'
import Share from '../../../components/Share'
import { AssetTypes, CollectionQuestionnaireSummaryType, CollectionType, ImageType } from '../../../lib/types'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import LoadingButton from '@mui/lab/LoadingButton'
import ImageIcon from '@mui/icons-material/Image'
import Button from '@mui/material/Button'
import ShareIcon from '@mui/icons-material/Share'
import DeleteIcon from '@mui/icons-material/Delete'
import CodeIcon from '@mui/icons-material/Code'
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone'
import api from '../../../lib/api'
import Typography from '@mui/material/Typography'
import { SimpleSnackbarContext } from '../../../components/SimpleSnackbar'
import { SimpleDialogContext } from '../../../components/SimpleDialog'
import { StepInviteRecorderContext } from '../../../components/StepInviteRecorder'
import { useHistory } from 'react-router-dom'
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Menu, MenuItem, TextField } from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import DownloadIcon from '@mui/icons-material/Download'
import Cookies from 'js-cookie'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
// @ts-ignore
import { CopyToClipboard } from 'react-copy-to-clipboard'
import IconButton from '@mui/material/IconButton'
import MoreIcon from '@mui/icons-material/MoreVert'
import CreateIcon from '@mui/icons-material/Create'
import Tooltip from '@mui/material/Tooltip'
import { usePermissions } from '../../../components/hooks/usePermissions'
import LinkIcon from '@mui/icons-material/Link'

interface iEditBar {
  canEdit: boolean
  id: string
  onChange?: (image: ImageType) => void
  refetch?: () => void
  collection?: CollectionType
  setEditMode: (editMode: boolean) => void
  showShare: boolean
  setShowShare: (show: boolean) => void
}

const EditBar: React.FC<iEditBar> = ({
  canEdit,
  id,
  onChange,
  collection,
  refetch,
  setEditMode,
  showShare,
  setShowShare
}) => {
  const embedCode = `
  <iframe
    width='640'
    height='360'
    src='${window.location.origin}/embed/collections/${id}'
    title='Rakonto'
    frameBorder='0'
    allow='camera; microphone; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    allowFullScreen
  ></iframe>
  `
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const { actions: dialogActions } = useContext(SimpleDialogContext)
  const { actions: recorderInviteActions } = useContext(StepInviteRecorderContext)
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null)
  const history = useHistory()
  const [progress, setProgress] = useState<number>(0)
  const [downloadLinksLoading, setDownloadLinksLoading] = useState<boolean>(false)
  const [questionnaireLinksModalOpen, setQuestionnaireLinksModalOpen] = useState<boolean>(false)
  const [collectionQuestionnaires, setCollectionQuestionnaires] = useState<CollectionQuestionnaireSummaryType[]>([])
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string>('')
  const [inviteStatus, setInviteStatus] = useState<string>('all')
  const token = Cookies.get('token') as string
  const canCopyEmbed = collection?.publicAcl

  const { isCreator } = usePermissions()
  const hasQuestionnaires = !!collection?.questionnaireId || !!collection?.questionnaires?.length

  const confirmAndDownloadQuestionnaireLinks = async (questionnaireId: string, status = 'all') => {
    const info = await api.getQuestionnaireInviteLinksExportInfo(questionnaireId, status)
    const download = async () => api.downloadQuestionnaireInviteLinks(questionnaireId, status)

    if (info.deadlineWarning) {
      dialogActions.open(
        'Questionnaire deadline is close',
        'This questionnaire has invite links with less than 10 days until the deadline. Do you want to download the CSV anyway?',
        {
          okText: 'Download CSV',
          showOk: true,
          cancelText: 'Cancel'
        },
        async success => {
          if (success) await download()
        }
      )
      return
    }

    await download()
  }

  const handleQuestionnaireLinksDownloadClick = async () => {
    try {
      setDownloadLinksLoading(true)
      const questionnaires = await api.getCollectionQuestionnaires(id)
      if (!questionnaires.length) {
        snackActions.open('This collection is not linked to a questionnaire.')
        return
      }

      if (questionnaires.length === 1) {
        await confirmAndDownloadQuestionnaireLinks(questionnaires[0].id)
        return
      }

      setCollectionQuestionnaires(questionnaires)
      setSelectedQuestionnaireId(questionnaires[0].id)
      setInviteStatus('all')
      setQuestionnaireLinksModalOpen(true)
    } catch (error) {
      snackActions.open('Internal server error.')
    } finally {
      setDownloadLinksLoading(false)
    }
  }

  const handleModalDownload = async () => {
    if (!selectedQuestionnaireId) return
    try {
      setDownloadLinksLoading(true)
      await confirmAndDownloadQuestionnaireLinks(selectedQuestionnaireId, inviteStatus)
      setQuestionnaireLinksModalOpen(false)
    } catch (error) {
      snackActions.open('Internal server error.')
    } finally {
      setDownloadLinksLoading(false)
    }
  }

  const onDrop: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void =
    async acceptedFiles => {
      try {
        const selectedFile = acceptedFiles[0]
        const image = await api.uploadImage(selectedFile, event => {
          setProgress(Math.round((event.loaded * 100) / event.total))
        })
        setProgress(0)
        onChange && onChange(image)
      } catch (error) {
        snackActions.open('Something was wrong! please try again.')
        setProgress(0)
      }
    }

  const {
    getRootProps,
    getInputProps,
    open: openUpload
  } = useDropzone({
    onDrop,
    noClick: true,
    noDrag: true,
    accept: 'image/png, image/jpeg'
  })

  const handleDelete = () => {
    dialogActions.open(
      'Delete forever',
      <>
        <Typography fontWeight="700">Are you sure you want to delete this collection?</Typography>
        <Typography> This action cannot be undone.</Typography>
      </>,
      {
        okText: 'Yes, delete',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await api.deleteCollection(id)
            history.push('/a/my-library')
          } catch (error) {
            snackActions.open(
              <>
                You cannot delete this collection because it has stories linked to it.
                <br />
                If you wish to delete this collection you first must remove all stories from it.
              </>
            )
          }
        }
      }
    )
  }

  const handleEdit = () => {
    setEditMode(true)
  }

  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null)
  }

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget)
  }

  const mobileMenuId = 'primary-search-account-menu-mobile'
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem
        onClick={() => {
          recorderInviteActions.open(collection || null)
          handleMobileMenuClose()
        }}
      >
        Request stories
      </MenuItem>
      <MenuItem
        onClick={() => {
          setShowShare(true)
          handleMobileMenuClose()
        }}
      >
        Share
      </MenuItem>
      {hasQuestionnaires && (
        <MenuItem
          onClick={() => {
            handleQuestionnaireLinksDownloadClick()
            handleMobileMenuClose()
          }}
        >
          Download Invites in CSV
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          handleDelete()
          handleMobileMenuClose()
        }}
        disabled={!isCreator}
      >
        Delete
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleEdit()
          handleMobileMenuClose()
        }}
      >
        Edit
      </MenuItem>
    </Menu>
  )

  if (!canEdit) return null
  return (
    <Box
      sx={{
        width: '100%',
        padding: '0 24px',
        marginBottom: 3,
        height: 65,
        display: 'flex',
        alignItems: 'center'
      }}
      component={Paper}
    >
      {showShare && (
        <Share
          id={id}
          title="Share"
          published={!!collection?.publicAcl}
          assetType={AssetTypes.collection}
          type="VIEWER"
          handleRefresh={() => {
            if (refetch) refetch()
          }}
          onCloseClick={() => {
            if (refetch) refetch()
            setShowShare(false)
          }}
          requireEmailToViewCollection={!!collection?.requiredEmail}
        />
      )}
      <Dialog open={questionnaireLinksModalOpen} onClose={() => setQuestionnaireLinksModalOpen(false)} fullWidth>
        <DialogTitle>Download questionnaire links CSV</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Questionnaire"
            value={selectedQuestionnaireId}
            onChange={event => setSelectedQuestionnaireId(event.target.value)}
          >
            {collectionQuestionnaires.map(questionnaire => (
              <MenuItem key={questionnaire.id} value={questionnaire.id}>
                {questionnaire.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Invites"
            value={inviteStatus}
            onChange={event => setInviteStatus(event.target.value)}
          >
            <MenuItem value="all">All invites</MenuItem>
            <MenuItem value="not_started">Not started</MenuItem>
            <MenuItem value="started">Started</MenuItem>
            <MenuItem value="not_completed">Not completed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionnaireLinksModalOpen(false)}>Cancel</Button>
          <LoadingButton loading={downloadLinksLoading} variant="contained" onClick={handleModalDownload}>
            Download CSV
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" {...getRootProps()}>
        <input {...getInputProps()} />
        <LoadingButton
          loadingPosition="start"
          loading={!!progress}
          onClick={openUpload}
          color="secondary"
          startIcon={<ImageIcon />}
        >
          Thumbnail
        </LoadingButton>
        <PopupState variant="popover">
          {popupState => (
            <React.Fragment>
              <Button
                startIcon={<DownloadIcon />}
                sx={{ display: { xs: 'none', md: 'flex' } }}
                color="secondary"
                {...bindTrigger(popupState)}
              >
                Download
              </Button>
              <Menu {...bindMenu(popupState)}>
                <MenuItem
                  onClick={() => {
                    const url = new URL(collection!.downloadUrl)
                    url.search = new URLSearchParams({ original: 't', jwt: token }).toString()
                    window.location.assign(url)
                    popupState.close()
                  }}
                >
                  Original stories media
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const url = new URL(collection!.downloadUrl)
                    url.search = new URLSearchParams({ original: 'f', jwt: token }).toString()
                    window.location.assign(url)

                    popupState.close()
                  }}
                >
                  Optimized stories media
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    const url = new URL(collection!.downloadTranscriptsUrl + 'plain')
                    url.search = new URLSearchParams({ jwt: token }).toString()
                    window.location.assign(url)
                    popupState.close()
                  }}
                >
                  All transcripts in Text
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const url = new URL(collection!.downloadTranscriptsUrl + 'csv')
                    url.search = new URLSearchParams({ jwt: token }).toString()
                    window.location.assign(url)
                    popupState.close()
                  }}
                >
                  All transcripts in CSV
                </MenuItem>
              </Menu>
            </React.Fragment>
          )}
        </PopupState>
        {hasQuestionnaires && (
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <LoadingButton
              color="secondary"
              loading={downloadLinksLoading}
              onClick={handleQuestionnaireLinksDownloadClick}
              startIcon={<DownloadIcon />}
            >
              Download Invites in CSV
            </LoadingButton>
          </Box>
        )}
        {canCopyEmbed && (
          <CopyToClipboard
            text={embedCode}
            startIcon={<CodeIcon />}
            options={{ format: 'text' }}
            onCopy={() => snackActions.open('Embded code copied to clipboard!')}
          >
            <Button color="secondary">Embed</Button>
          </CopyToClipboard>
        )}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Button
            color="secondary"
            onClick={() => recorderInviteActions.open(collection || null)}
            startIcon={<PersonAddIcon />}
          >
            Request stories
          </Button>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Button color="secondary" onClick={() => setShowShare(true)} startIcon={<ShareIcon />}>
            Share
          </Button>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Button color="secondary" disabled={!isCreator} onClick={handleDelete} startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Tooltip arrow title="Edit Collection Title and Description">
            <Button color="secondary" onClick={handleEdit} startIcon={<CreateIcon />}>
              Edit
            </Button>
          </Tooltip>
        </Box>
        {collection?.questionnaireId && (
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Tooltip arrow title="This Collection is Linked to a Questionnaire">
              <Button
                color="secondary"
                onClick={() => {
                  const baseUrl = window.location.origin // Obtém a URL base atual
                  const questionnaireUrl = `${baseUrl}/a/questionnaire/${collection?.questionnaireId}`
                  window.open(questionnaireUrl, '_blank') // Abre o link em uma nova aba
                }}
                startIcon={<LinkIcon />}
              >
                Go to the Questionnaire
              </Button>
            </Tooltip>
          </Box>
        )}
        <div style={{ flex: '1' }} />
        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <IconButton
            size="large"
            aria-label="show more"
            aria-controls={mobileMenuId}
            aria-haspopup="true"
            onClick={handleMobileMenuOpen}
            color="inherit"
          >
            <MoreIcon />
          </IconButton>
        </Box>
      </Stack>
      {renderMobileMenu}
    </Box>
  )
}

export default EditBar
